import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import BrandModal from "../components/BrandModal";
import ConfirmDialog from "../components/ConfirmDialog";
import Toast from "../components/Toast";
import { apiErrorMessage } from "../services/api";
import { deleteBrand, fetchBrands } from "../services/catalogService";

export default function Brands() {
  const { t, i18n } = useTranslation();

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setBrands(await fetchBrands());
    } catch (err) {
      setError(apiErrorMessage(err, t("brands.loadFailed")));
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteBrand(deleting.id);
      setToast({ type: "success", message: t("brands.deleted", { name: deleting.name }) });
      setDeleting(null);
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("brands.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("brands.title")}</h1>
          <p className="text-sm text-ink-2">{t("brands.subtitle")}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="btn-primary"
        >
          {t("brands.new")}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading ? (
          <p className="px-4 py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : brands.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-ink-2">{t("brands.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="px-4 py-3">{t("brands.table.name")}</th>
                  <th className="px-4 py-3">{t("brands.table.products")}</th>
                  <th className="px-4 py-3">{t("products.table.status")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {brands.map((brand) => (
                  <tr key={brand.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {brand.logo ? (
                          <img src={brand.logo} alt="" className="h-8 w-8 rounded-lg object-cover" />
                        ) : (
                          <span className="grid h-8 w-8 place-items-center rounded-lg bg-raised text-xs text-ink-2">
                            —
                          </span>
                        )}
                        <span className="font-medium">{brand.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-2">{brand.products_count ?? 0}</td>
                    <td className="px-4 py-3">
                      {brand.is_active ? (
                        <span className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text">
                          {t("products.active")}
                        </span>
                      ) : (
                        <span className="rounded-full bg-danger-wash px-3 py-1 text-xs font-medium text-danger-text">
                          {t("products.inactive")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-end">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(brand);
                            setModalOpen(true);
                          }}
                          className="text-sm font-medium text-accent-text hover:underline"
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteError("");
                            setDeleting(brand);
                          }}
                          className="text-sm font-medium text-danger-text hover:underline"
                        >
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BrandModal
        brand={editing}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(saved, wasEditing) => {
          setToast({
            type: "success",
            message: wasEditing
              ? t("brands.updated", { name: saved.name })
              : t("brands.created", { name: saved.name }),
          });
          load();
        }}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t("brands.deleteTitle")}
        message={t("brands.deleteMessage", { name: deleting?.name ?? "" })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
